import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  TextField,
  Typography
} from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Optimized icon imports
import {
  AccountCircle as AccountCircleIcon,
  FullscreenOutlined as FullscreenOutlinedIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  NotificationsNoneOutlined as NotificationsNoneOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon
} from '@mui/icons-material';

// Custom Components
import { DrawerContent } from './DrawerContent';
import { LiveClock } from './LiveClock';

// Utils and Hooks
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { generateBreadcrumbs, getGreeting, getInitials } from './BreadcrumbUtils';
import { useDrawer } from './DrawerContext';

// Assets and Services
import axiosInstance from 'Services/APIService';
import { fetchGroupsList, logout } from 'store/slices/loginSignupSlice';
import Logo from '../../assets/appLogos/UTrust.png';
import { breadcrumbRoutes } from '../../commonComponents/BreadcrumbConfig';
import {
  raiseGroupJoinRequest,
  listPublicGroups,
  getFundInvestmentGroupContext
} from 'api/fundInvestmentManagement';
import type { Group as SignupGroup } from 'types/loginSignUpTypes';

// Types
interface UserDetails {
  avatar?: string;
  name?: string;
  groups?: Array<string | { group_id?: string; groupId?: string; id?: string }>;
  user?: {
    name: string;
    groups?: Array<string | { group_id?: string; groupId?: string; id?: string }>;
  };
  role?: Array<{ role_id: string; label: string }>;
}

const resolveMembershipGroupIds = (details: UserDetails | null): string[] => {
  let source: Array<string | { group_id?: string; groupId?: string; id?: string }> = [];
  const groups = details?.groups;
  const nestedGroups = details?.user?.groups;

  if (Array.isArray(groups)) {
    source = groups;
  } else if (Array.isArray(nestedGroups)) {
    source = nestedGroups;
  }

  const ids = source
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      return entry?.group_id || entry?.groupId || entry?.id || '';
    })
    .filter((value): value is string => !!value);

  return Array.from(new Set(ids));
};

// Memoized styled components
const StyledDrawer = memo(
  styled('div', {
    shouldForwardProp: (prop) => prop !== 'drawerOpen'
  })<{ drawerOpen: boolean }>(({ theme, drawerOpen }) => ({
    width: drawerOpen ? 280 : 64,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    position: 'fixed',
    top: 0,
    height: '100vh',
    zIndex: 1200,
    background: 'linear-gradient(180deg, #ffffff 0%, #f8faff 100%)',
    boxShadow: '4px 0 20px rgba(0,0,0,0.08)',
    transition: theme.transitions.create(['width'], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard
    }),
    overflowX: 'hidden',
    '&::-webkit-scrollbar': {
      width: '6px'
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      background: alpha(theme.palette.primary.main, 0.2),
      borderRadius: '3px',
      '&:hover': {
        background: alpha(theme.palette.primary.main, 0.3)
      }
    }
  }))
);

// Memoized header paper component
const HeaderPaper = memo(
  styled(Paper)(({ theme }) => ({
    padding: '0 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 1300,
    position: 'fixed',
    width: '100%',
    height: 70,
    background: 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    backdropFilter: 'blur(10px)',
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    [theme.breakpoints.down('sm')]: {
      padding: '0 12px',
      height: 60
    }
  }))
);

// Performance optimized button component
const ActionButton = memo(
  styled(IconButton)(({ theme }) => ({
    backgroundColor: alpha(theme.palette.info.main, 0.1),
    color: theme.palette.info.main,
    borderRadius: '12px',
    padding: '8px',
    '&:hover': {
      backgroundColor: alpha(theme.palette.info.main, 0.2),
      transform: 'translateY(-2px)'
    },
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    [theme.breakpoints.down('sm')]: {
      padding: '6px'
    }
  }))
);

// Custom hooks for better performance
const useUserDetails = (): UserDetails | null => {
  return useMemo(() => {
    const loginInfo = localStorage.getItem('user-details');
    return loginInfo ? JSON.parse(loginInfo) : null;
  }, []);
};

const useAvatar = (userDetails: UserDetails | null): string | null => {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    let currentUrl: string | null = null;

    const fetchAvatar = async () => {
      if (!userDetails?.avatar) return;

      try {
        const response = await axiosInstance.get(
          `/avatar/get-avatar?path=${encodeURIComponent(userDetails.avatar)}`,
          { responseType: 'blob' }
        );

        if (isMounted) {
          const imageUrl = URL.createObjectURL(response.data);
          currentUrl = imageUrl;
          setAvatarUrl(imageUrl);
        }
      } catch (error) {
        console.error('Error fetching avatar:', error);
        if (isMounted) {
          setAvatarUrl(null);
        }
      }
    };

    fetchAvatar();

    return () => {
      isMounted = false;
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [userDetails?.avatar]);

  return avatarUrl;
};

// Animation variants - moved outside component to prevent recreation
const drawerVariants = {
  open: {
    x: 0,
    transition: { type: 'spring', stiffness: 300, damping: 30 }
  },
  closed: {
    x: -280,
    transition: { type: 'spring', stiffness: 300, damping: 30 }
  }
};

const backdropVariants = {
  open: { opacity: 1, transition: { duration: 0.3 } },
  closed: { opacity: 0, transition: { duration: 0.3 } }
};

interface HeaderSidebarProps {
  username_initials: string;
}

const HeaderSidebar: React.FC<HeaderSidebarProps> = memo(({ username_initials }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationCount] = useState(3);
  const [showJoinCommunityDialog, setShowJoinCommunityDialog] = useState(false);
  const [selectedJoinGroupIds, setSelectedJoinGroupIds] = useState<string[]>([]);
  const [joinMessage, setJoinMessage] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinSubmitting, setJoinSubmitting] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinSuccessMessage, setJoinSuccessMessage] = useState('');
  const [joinCommunityOptions, setJoinCommunityOptions] = useState<SignupGroup[]>([]);

  const { drawerOpen, setDrawerOpen, isMobile, drawerWidth } = useDrawer();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const signupGroups = useAppSelector((state) => state.loginSignup.groups || []);
  const theme = useTheme();

  const userDetails = useUserDetails();
  const avatarUrl = useAvatar(userDetails);
  const userGroupIds = useMemo(() => resolveMembershipGroupIds(userDetails), [userDetails]);
  const hasExistingMembership = userGroupIds.length > 0;

  const filteredJoinCommunityOptions = useMemo<SignupGroup[]>(() => {
    if (!hasExistingMembership) return joinCommunityOptions;
    return joinCommunityOptions.filter((group) => !userGroupIds.includes(group.group_id));
  }, [hasExistingMembership, joinCommunityOptions, userGroupIds]);

  const currentCommunity = useMemo(() => {
    const context = getFundInvestmentGroupContext();
    if (context) {
      const found = joinCommunityOptions.find((group) => group.group_id === context.groupId);
      if (found) return found;
    }
    const userJoinedGroups = joinCommunityOptions.filter((group) => userGroupIds.includes(group.group_id));
    return userJoinedGroups[0] || null;
  }, [joinCommunityOptions, userGroupIds]);

  const open = Boolean(anchorEl);
  const breadcrumbs = useMemo(
    () => generateBreadcrumbs(location.pathname, breadcrumbRoutes),
    [location.pathname]
  );

  const toggleDrawer = useCallback(() => {
    if (isMobile) {
      setMobileOpen((prev: boolean) => !prev);
    } else {
      setDrawerOpen(!drawerOpen); // Fixed: Direct boolean assignment
    }
  }, [isMobile, setDrawerOpen, drawerOpen]);

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const loadJoinCommunityStates = useCallback(async () => {
    try {
      setJoinLoading(true);
      setJoinError(null);

      const publicGroups = await listPublicGroups();
      const mapped: SignupGroup[] = publicGroups.map((g) => ({
        group_id: g.group_id,
        label: g.label,
        description: g.description,
        avatar: g.avatar || '',
        peopleCount: g.peopleCount,
        servicesCount: g.servicesCount
      }));
      setJoinCommunityOptions(mapped);
    } catch (error) {
      setJoinCommunityOptions(signupGroups);
      setJoinError(error instanceof Error ? error.message : 'Failed to load communities.');
    } finally {
      setJoinLoading(false);
    }
  }, [signupGroups]);

  const handleOpenJoinCommunity = useCallback(() => {
    void loadJoinCommunityStates();
    setSelectedJoinGroupIds([]);
    setJoinMessage('');
    setJoinSuccessMessage('');
    setJoinError(null);
    setShowJoinCommunityDialog(true);
    handleClose();
  }, [handleClose, loadJoinCommunityStates]);

  const handleToggleJoinCommunityGroup = useCallback((groupId: string) => {
    setSelectedJoinGroupIds((current) =>
      current.includes(groupId) ? current.filter((id) => id !== groupId) : [...current, groupId]
    );
  }, []);

  const handleRaiseJoinCommunityRequest = useCallback(async () => {
    if (selectedJoinGroupIds.length === 0) {
      setJoinError('Please select at least one community.');
      return;
    }

    const selectedGroups = joinCommunityOptions.filter((group) => selectedJoinGroupIds.includes(group.group_id));
    if (selectedGroups.length === 0) {
      setJoinError('Selected communities were not found. Please refresh and try again.');
      return;
    }

    try {
      setJoinSubmitting(true);
      setJoinError(null);

      const requestResults = await Promise.allSettled(
        selectedGroups.map((group) =>
          raiseGroupJoinRequest({
            groupId: group.group_id,
            serviceId: 'fundManagementInvestment',
            requestMessage: joinMessage.trim() || undefined
          })
        )
      );

      const succeededGroups = selectedGroups.filter((_, index) => requestResults[index].status === 'fulfilled');
      const failedGroups = selectedGroups.filter((_, index) => requestResults[index].status === 'rejected');

      if (succeededGroups.length > 0) {
        if (succeededGroups.length === 1) {
          setJoinSuccessMessage(`Join request created for ${succeededGroups[0].label}.`);
        } else {
          setJoinSuccessMessage(`Join requests created for ${succeededGroups.length} communities.`);
        }
        
        setTimeout(() => {
          setShowJoinCommunityDialog(false);
        }, 1500);
      }

      if (failedGroups.length > 0) {
        const failedLabels = failedGroups.slice(0, 3).map((group) => group.label).join(', ');
        const suffix = failedGroups.length > 3 ? ' and more.' : '.';
        setJoinError(`Some requests failed: ${failedLabels}${suffix}`);
      }

      await loadJoinCommunityStates();
      setSelectedJoinGroupIds([]);
    } catch (error) {
      setJoinError(error instanceof Error ? error.message : 'Failed to raise join request.');
    } finally {
      setJoinSubmitting(false);
    }
  }, [joinCommunityOptions, joinMessage, loadJoinCommunityStates, selectedJoinGroupIds]);

  const handleSettingsClick = useCallback(() => {
    navigate('/profile-settings');
    handleClose();
  }, [navigate, handleClose]);

  const handleLogout = useCallback(() => {
    dispatch(logout());
    handleClose();
    navigate('/', { replace: true });
  }, [dispatch, navigate, handleClose]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.log('Error attempting to enable fullscreen mode:', err);
      });
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  return (
    <>
      {/* Header Section */}
      <HeaderPaper square>
        {/* Left Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <ActionButton
              onClick={toggleDrawer}
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  transform: 'rotate(180deg)'
                }
              }}
            >
              <MenuIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
            </ActionButton>
          </motion.div>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <motion.div
              style={{
                height: '115px',
                display: 'flex',
                alignItems: 'center'
              } as any}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <img
                src={Logo}
                alt="Logo"
                style={{
                  height: '115px',
                  objectFit: 'contain'
                }}
              />
            </motion.div>
          </Box>
        </Box>

        {/* Center Section */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', flex: 1 }}>
          <LiveClock />
        </Box>

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Notifications */}
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <ActionButton>
              <Badge
                badgeContent={notificationCount}
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '10px',
                    minWidth: '18px',
                    height: '18px'
                  }
                }}
              >
                <NotificationsNoneOutlinedIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />
              </Badge>
            </ActionButton>
          </motion.div>

          {/* Fullscreen */}
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <ActionButton onClick={toggleFullscreen}>
              <FullscreenOutlinedIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />
            </ActionButton>
          </motion.div>

          {/* Profile */}
          <ProfileSection
            onClick={handleClick}
            userDetails={userDetails}
            avatarUrl={avatarUrl}
            username_initials={username_initials}
            theme={theme}
          />
        </Box>
      </HeaderPaper>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {isMobile && mobileOpen && (
          <MobileDrawer drawerWidth={drawerWidth} onClose={() => setMobileOpen(false)} />
        )}
      </AnimatePresence>

      {/* Desktop Drawer */}
      {!isMobile && (
        <StyledDrawer drawerOpen={drawerOpen}>
          <DrawerContent isCollapsed={!drawerOpen} />
        </StyledDrawer>
      )}

      {/* Profile Popover */}
      <ProfilePopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        userDetails={userDetails}
        avatarUrl={avatarUrl}
        username_initials={username_initials}
        onSettingsClick={handleSettingsClick}
        onOpenJoinCommunity={handleOpenJoinCommunity}
        onLogout={handleLogout}
      />

      <Dialog
        open={showJoinCommunityDialog}
        onClose={() => setShowJoinCommunityDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Join Community</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: 1 }}>
          {hasExistingMembership ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              You are already part of <strong>{currentCommunity?.label || userGroupIds[0]}</strong>. You can request to join additional communities.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              Select one or more communities and submit your join request.
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ color: '#334155', fontWeight: 700 }}>
            Select a community
          </Typography>

          {joinLoading && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Loading your groups and join status...
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gap: 1.25,
              maxHeight: { xs: 400, sm: 500 },
              overflowY: 'auto',
              pr: 0.5,
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }
            }}
          >
            {filteredJoinCommunityOptions.map((group) => {
              const isSelected = selectedJoinGroupIds.includes(group.group_id);
              const canSelect = true;

              return (
                <Paper
                  key={group.group_id}
                  onClick={() => handleToggleJoinCommunityGroup(group.group_id)}
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 2,
                    cursor: canSelect ? 'pointer' : 'default',
                    border: '1px solid',
                    borderColor: isSelected ? '#3b82f6' : '#e2e8f0',
                    backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                    transition: 'all .2s ease',
                    '&:hover': {
                      borderColor: canSelect ? '#60a5fa' : '#e2e8f0',
                      boxShadow: canSelect ? '0 4px 14px rgba(59, 130, 246, 0.12)' : 'none'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '1px solid #dbeafe',
                        background: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {group.avatar ? (
                        <img
                          src={group.avatar}
                          alt={group.label}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <AccountCircleIcon sx={{ color: '#94a3b8', fontSize: 28 }} />
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }} noWrap>
                        {group.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                        {(group.peopleCount || 0).toLocaleString()} Members
                      </Typography>
                    </Box>
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      color: '#475569',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      minHeight: 32
                    }}
                  >
                    {group.description || 'Community group'}
                  </Typography>

                  {isSelected && (
                    <Chip
                      size="small"
                      label="Selected"
                      sx={{
                        mt: 1,
                        borderRadius: 999,
                        backgroundColor: '#dbeafe',
                        color: '#1d4ed8',
                        border: '1px solid #93c5fd',
                        fontWeight: 700
                      }}
                    />
                  )}
                </Paper>
              );
            })}

            {!joinLoading && filteredJoinCommunityOptions.length === 0 && (
              <Paper sx={{ px: 2, py: 2, borderRadius: 2, border: '1px dashed #cbd5e1', backgroundColor: '#f8fafc' }}>
                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
                  No communities found.
                </Typography>
              </Paper>
            )}
          </Box>

          <TextField
            label="Request Message"
            value={joinMessage}
            onChange={(event) => setJoinMessage(event.target.value)}
            multiline
            minRows={3}
            placeholder="I would like to join this community"
          />

          {joinError && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {joinError}
            </Alert>
          )}

          {joinSuccessMessage && (
            <Chip
              label={joinSuccessMessage}
              sx={{
                borderRadius: 2,
                backgroundColor: '#f0fdf4',
                color: '#166534',
                border: '1px solid #86efac',
                fontWeight: 700
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setShowJoinCommunityDialog(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleRaiseJoinCommunityRequest()}
            disabled={
              joinLoading ||
              joinSubmitting ||
              filteredJoinCommunityOptions.length === 0 ||
              selectedJoinGroupIds.length === 0
            }
            sx={{ textTransform: 'none', backgroundColor: '#16a34a', '&:hover': { backgroundColor: '#15803d' } }}
          >
            {joinSubmitting ? 'Requesting...' : 'Request to Join'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

// Extracted Profile Section Component
interface ProfileSectionProps {
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  userDetails: UserDetails | null;
  avatarUrl: string | null;
  username_initials: string;
  theme: any;
}

const ProfileSection = memo(
  ({ onClick, userDetails, avatarUrl, username_initials, theme }: ProfileSectionProps) => (
    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.1
          )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          borderRadius: '20px',
          padding: '3px',
          cursor: 'pointer',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          backdropFilter: 'blur(10px)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.2)}`
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        onClick={onClick}
      >
        <Avatar
          avatarUrl={avatarUrl}
          userDetails={userDetails}
          username_initials={username_initials}
          theme={theme}
        />
        <IconButton sx={{ color: theme.palette.primary.main, padding: '4px' }}>
          <SettingsOutlinedIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />
        </IconButton>
      </Box>
    </motion.div>
  )
);

// Extracted Avatar Component
interface AvatarProps {
  avatarUrl: string | null;
  userDetails: UserDetails | null;
  username_initials: string;
  theme: any;
}

const Avatar = memo(({ avatarUrl, userDetails, username_initials, theme }: AvatarProps) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: { xs: 28, sm: 32 },
      height: { xs: 28, sm: 32 },
      borderRadius: '50%',
      background: avatarUrl
        ? 'transparent'
        : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
      color: '#fff',
      fontWeight: 700,
      fontSize: { xs: '12px', sm: '14px' },
      overflow: 'hidden',
      border: `2px solid ${alpha(theme.palette.common.white, 0.8)}`,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}
  >
    {avatarUrl ? (
      <img
        src={avatarUrl}
        alt="User Avatar"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
      />
    ) : userDetails?.user?.name ? (
      getInitials(userDetails.user.name)
    ) : (
      username_initials
    )}
  </Box>
));

// Extracted Mobile Drawer Component
interface MobileDrawerProps {
  drawerWidth: number;
  onClose: () => void;
}

const MobileDrawer = memo(({ drawerWidth, onClose }: MobileDrawerProps) => (
  <>
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.4) 100%)',
        backdropFilter: 'blur(4px)',
        zIndex: 1250,
        cursor: 'pointer'
      }}
      onClick={onClose}
    />
    <motion.div
      initial="closed"
      animate="open"
      exit="closed"
      variants={drawerVariants}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: 1300,
        width: drawerWidth,
        background: 'linear-gradient(180deg, #ffffff 0%, #f8faff 100%)',
        boxShadow: '4px 0 30px rgba(0,0,0,0.15)',
        borderRadius: '0 20px 20px 0'
      }}
    >
      <DrawerContent isMobileDrawer={true} onClose={onClose} />
    </motion.div>
  </>
));

// Extracted Profile Popover Component
interface ProfilePopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  userDetails: UserDetails | null;
  avatarUrl: string | null;
  username_initials: string;
  onSettingsClick: () => void;
  onOpenJoinCommunity: () => void;
  onLogout: () => void;
}

const ProfilePopover = memo(
  ({
    open,
    anchorEl,
    onClose,
    userDetails,
    avatarUrl,
    username_initials,
    onSettingsClick,
    onOpenJoinCommunity,
    onLogout
  }: ProfilePopoverProps) => {
    const navigate = useNavigate();

    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        disableScrollLock={true}
      >
        <Card sx={{ width: '300px', borderRadius: '12px', boxShadow: 4 }}>
          <CardContent sx={{ p: '20px', backgroundColor: '#f9fafb' }}>
            {/* Profile Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: '20px' }}>
              <Avatar
                avatarUrl={avatarUrl}
                userDetails={userDetails}
                username_initials={username_initials}
                theme={{
                  palette: {
                    primary: { main: '#3e4e61' },
                    secondary: { main: '#3e4e61' },
                    common: { white: '#ffffff' }
                  }
                }}
              />
              <Typography variant="h6" color="text.primary" sx={{ fontWeight: 'bold', ml: 1.5 }}>
                {getGreeting()}, {userDetails?.name || userDetails?.user?.name}
              </Typography>
            </Box>

            {/* Role Chips */}
            <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
              {userDetails?.role?.map((roleItem, index: number) => (
                <Chip
                  key={roleItem.role_id || index}
                  label={roleItem.label}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', fontWeight: 500, borderRadius: '8px', height: 24 }}
                />
              ))}
            </Box>

            {/* Upgrade Button */}
            <Button
              fullWidth
              variant="contained"
              color="primary"
              sx={{
                mt: '12px',
                backgroundColor: '#4a90e2',
                '&:hover': { backgroundColor: '#357abd' },
                p: '12px'
              }}
              onClick={() => alert('Upgrade to Premium')}
            >
              Upgrade Your Plan
            </Button>

            <Button
              fullWidth
              variant="outlined"
              sx={{
                mt: '10px',
                borderColor: '#86efac',
                color: '#166534',
                backgroundColor: '#f0fdf4',
                '&:hover': { borderColor: '#4ade80', backgroundColor: '#dcfce7' },
                p: '12px',
                fontWeight: 700,
                textTransform: 'none'
              }}
              onClick={onOpenJoinCommunity}
            >
              Join Community
            </Button>

            <Divider sx={{ margin: '16px 0', width: '100%' }} />

            {/* Promo Image */}
            <img
              src="https://plus.unsplash.com/premium_photo-1669927131902-a64115445f0f?q=80&w=2075&auto=format&fit=crop&ixlib=rb-4.0.3"
              alt="Promo"
              style={{ width: '100%', borderRadius: '8px', marginBottom: '16px' }}
            />

            {/* Menu Items */}
            <MenuItem
              onClick={onSettingsClick}
              sx={{ display: 'flex', alignItems: 'center', ml: 0 }}
            >
              <IconButton sx={{ color: '#4a90e2', mr: '12px' }}>
                <SettingsOutlinedIcon sx={{ fontSize: '20px' }} />
              </IconButton>
              Account Settings
            </MenuItem>

            <MenuItem
              onClick={() => {
                onClose();
                navigate('Users-SocialProfile');
              }}
              sx={{ display: 'flex', alignItems: 'center', ml: 0 }}
            >
              <IconButton sx={{ color: '#4a90e2', mr: '12px' }}>
                <AccountCircleIcon sx={{ fontSize: '20px' }} />
              </IconButton>
              Social Profile
            </MenuItem>

            <MenuItem onClick={onLogout} sx={{ display: 'flex', alignItems: 'center', ml: 0 }}>
              <IconButton sx={{ color: '#4a90e2', mr: '12px' }}>
                <LogoutIcon sx={{ fontSize: '20px' }} />
              </IconButton>
              Logout
            </MenuItem>
          </CardContent>
        </Card>
      </Popover>
    );
  }
);

HeaderSidebar.displayName = 'HeaderSidebar';
export default HeaderSidebar;
